import { signInWithCustomToken } from "firebase/auth";
import { doc, DocumentReference } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth, useFirestore, useFirestoreDocDataOnce } from "reactfire";
import { PollValue } from "../../../functions/src/schema";
import Loader from "../../components/Loader";

export default function ConfirmScreen() {
  const params = new URLSearchParams(window.location.search);
  const pollId = params.get("pollId") || "";
  const uid = params.get("uid") || "";
  const vote = params.get("vote") || "";
  const authToken = params.get("authToken");
  const auth = useAuth();
  const firestore = useFirestore();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<string | null>();

  // Create a reference to the poll document - always create it even if pollId is empty
  // The hook will handle the case when pollId is empty
  const pollDoc = doc(
    firestore,
    `polls/${pollId}`
  ) as DocumentReference<PollValue>;

  // Always call the hook, handle invalid cases in the component
  const { data: poll, status } = useFirestoreDocDataOnce(pollDoc);

  // Handle navigation based on parameters and data
  useEffect(() => {
    // Redirect to 404 if required parameters are missing
    if (!pollId || !uid || !vote) {
      window.location.href = "/404";
      return;
    }

    // Redirect to 404 if poll doesn't exist and we've finished loading
    if (status === "success" && !poll) {
      window.location.href = "/404";
      return;
    }
  }, [pollId, uid, vote, poll, status]);

  // Sign in with the auth token if available
  useEffect(() => {
    if (authToken && !auth.currentUser && !isAuthenticating) {
      setIsAuthenticating(true);
      signInWithCustomToken(auth, authToken)
        .then(() => {
          console.log("Successfully signed in with custom token");
        })
        .catch((error) => {
          console.error("Error signing in with custom token:", error);
          setAuthError("Failed to authenticate. Please try again.");
        })
        .finally(() => {
          setIsAuthenticating(false);
        });
    }
  }, [auth, authToken, isAuthenticating]);

  // Redirect when captcha is completed
  useEffect(() => {
    if (captcha && pollId && uid && vote) {
      const url = `https://us-central1-deals-7301a.cloudfunctions.net/onVoteReceived?pollId=${pollId}&uid=${uid}&vote=${vote}&captcha=${captcha}`;
      window.location.href = url;
    }
  }, [captcha, uid, vote, pollId]);

  // Show loader while authenticating or loading poll data
  if (isAuthenticating || (status === "loading" && !poll)) {
    return <Loader visible />;
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <h2>Authentication Error</h2>
        <p>{authError}</p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 600 }}>
        <h1 style={{ marginTop: 20 }}>Hey there, hitchhiker!</h1>
        <p>
          We need to confirm that you're human, so your vote can be counted.
          This is because some email clients auto-click links in emails 🤖.
        </p>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <ReCAPTCHA
            onChange={setCaptcha}
            sitekey="6LeSliMnAAAAAEQHBraI7-Mmo3GregrWdu-L041H"
          />
        </div>
      </div>
    </div>
  );
}
